import {
  Avatar,
  createStyles,
  Theme,
  WithStyles,
  withStyles,
} from "@material-ui/core";
import MenuItem from "@material-ui/core/MenuItem";
import MenuList from "@material-ui/core/MenuList";
import Typography from "@material-ui/core/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LogoutIcon from "@mui/icons-material/Logout";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import React, { RefObject } from "react";
import { version } from "../../../../../package.json";
import { useTranslations } from "../../../../adapters/i18n";
import userUtils from "../../../../shared/userUtils";
import { LogoutModal } from "../LogoutModal";
import { MenuButton, MenuButtonTrigger, useMenuButton } from "../MenuButton";

const ID = "user-dropdown";

export type UserDropdownProps = {
  isLogoutModalOpen: boolean;
  onLogout: () => void;
  toggleLogoutModal: (isOpen: boolean) => void;
  userName: string;
} & WithStyles<typeof styles>;

const UserDropdown = ({
  classes,
  isLogoutModalOpen,
  onLogout,
  toggleLogoutModal,
  userName,
}: UserDropdownProps) => {
  const { handleClickAway, handleToggleMenu, isMenuOpen, triggerRef } =
    useMenuButton();

  const handleCloseLogoutModal = () => {
    toggleLogoutModal(false);
  };

  const handleExitOptionClick = () => {
    toggleLogoutModal(true);
  };

  return (
    <>
      <MenuButton
        id={ID}
        isOpen={isMenuOpen}
        onClickAway={handleClickAway}
        placement="bottom-end"
        trigger={
          <UserButton
            classes={classes}
            isMenuOpen={isMenuOpen}
            onClick={handleToggleMenu}
            triggerRef={triggerRef}
            userName={userName}
          />
        }
        triggerRef={triggerRef}
      >
        <DropdownOptions
          classes={classes}
          onExitClick={handleExitOptionClick}
        />
      </MenuButton>
      <LogoutModal
        isOpen={isLogoutModalOpen}
        onClose={handleCloseLogoutModal}
        onLogout={onLogout}
      />
    </>
  );
};

type UserButtonProps = {
  isMenuOpen: boolean;
  onClick: () => void;
  triggerRef: RefObject<HTMLButtonElement>;
  userName: string;
} & WithStyles<typeof styles>;

const UserButton = ({
  classes,
  isMenuOpen,
  onClick,
  triggerRef,
  userName,
}: UserButtonProps) => {
  const { t } = useTranslations();

  const abbreviatedUsername = userUtils
    .abbreviateUsername(userName)
    .toLocaleLowerCase();
  const userInitials = userUtils.getUserInitials(userName);

  return (
    <MenuButtonTrigger
      aria-label={t("common:user-options")}
      className={classes.button}
      isListOpen={isMenuOpen}
      listId={ID}
      onClick={onClick}
      ref={triggerRef}
    >
      <div className={classes.buttonContentWrapper}>
        <Avatar className={classes.avatar}>{userInitials}</Avatar>
        <Typography variant="caption" className={classes.userName}>
          {abbreviatedUsername}
        </Typography>

        <ExpandMoreIcon className={classes.expandIcon} />
      </div>
    </MenuButtonTrigger>
  );
};

type DropdownOptionsProps = { onExitClick: () => void } & WithStyles<
  typeof styles
>;

const DropdownOptions = ({ classes, onExitClick }: DropdownOptionsProps) => {
  const { t } = useTranslations();

  return (
    <MenuList>
      <MenuItem
        aria-readonly="true"
        data-testid="portal-version"
        className={classes.plainListOption}
        tabIndex={-1}
      >
        <SwapHorizIcon className={classes.listOptionIcon} />
        <Typography className={classes.listOptionText}>
          {t("common:portal-version", { version })}
        </Typography>
      </MenuItem>
      <MenuItem
        className={classes.listOption}
        data-testid="logout-option"
        onClick={onExitClick}
      >
        <LogoutIcon className={classes.listOptionIcon} />
        <Typography className={classes.listOptionText}>
          {t("common:actions.exit")}
        </Typography>
      </MenuItem>
    </MenuList>
  );
};

const styles = (theme: Theme) => {
  const defaultListOptionStyle = {
    alignItems: "center",
    flexDirection: "row",
    fontSize: "16px",
    display: "flex",
    gap: "24px",
    padding: "8px 16px",
    backgroundColor: theme.palette.common.white,
    color: "#616161",
    fontWeight: 400,
  } as const;

  return createStyles({
    button: {
      height: "100%",
    },
    buttonContentWrapper: {
      alignItems: "center",
      display: "flex",
      flexDirection: "row",
      gap: "16px",
    },
    avatar: {
      backgroundColor: "#CBD938",
      fontSize: 14,
      width: 36,
      height: 36,
    },
    userName: {
      fontSize: "12px",
      lineHeight: "24px",
      margin: "0 8px",
      color: "#666666",
      textTransform: "capitalize",
      whiteSpace: "nowrap",
      "@media (max-width: 1280px)": {
        display: "none",
      },
    },
    expandIcon: {
      color: "#616161",
      fontSize: "24px !important",
      "@media (max-width: 1280px)": {
        display: "none",
      },
    },
    listOption: {
      ...defaultListOptionStyle,
    },
    plainListOption: {
      ...defaultListOptionStyle,
      cursor: "default",
      pointerEvents: "none",
    },
    listOptionIcon: {
      color: "inherit",
      fontSize: "24px !important",
    },
    listOptionText: {
      color: "inherit",
    },
  });
};

export default withStyles(styles)(UserDropdown);
